/*  MUI  */
import { Box, Grid, TableCell, TableRow, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { ApplicationNameAdminAppModalCell } from 'application/Shared/Components/ApplicationNameAdminAppModalCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { HMS } from 'application/Shared/Components/Usage';
import { Application } from 'objects/Application';

/*  React  */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

export const ApplicationMapReports = (props) => {
    const { renderingScheduledReport, reportData, reportSetup, runReport } =
        props;

    if (typeof reportData === 'undefined') {
        return null;
    }
    return (
        <Box data-trackid="applicationMapReportsMainRender">
            {reportData.applicationMapOverview && (
                <ApplicationReportsMap
                    {...props}
                    renderingScheduledReport={renderingScheduledReport}
                    reportSetup={reportSetup}
                    reportData={reportData.applicationMapOverview}
                />
            )}
            {reportData.applicationMapBreakdown && (
                <ApplicationReportsMapBreakdown
                    renderingScheduledReport={renderingScheduledReport}
                    reportData={reportData.applicationMapBreakdown}
                    runReport={runReport}
                />
            )}
        </Box>
    );
};

const ApplicationReportsMap = (props) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { reportData, reportSetup } = props;
    if (!reportData) {
        return null;
    }

    let data = reportData.data.map((row) => ({
        baseline: reportData.baseline,
        name: row.application.name,
        size: row.size
    }));
    data = data.sort((a, b) => b.size - a.size).slice(0, 20);

    let userData = reportData.data.map((row) => ({
        baselineUsers: reportData.baselineUsers,
        name: row.application.name,
        size: row.users
    }));
    userData = userData.sort((a, b) => b.size - a.size).slice(0, 20);

    let selectedApplicationName = '';
    const selectedApplications = reportData.data.filter(
        (row) =>
            reportSetup.applicationSelection.indexOf(row.application.id) > -1
    );
    if (selectedApplications.length === 0) {
        selectedApplicationName = 'N/A';
    }
    if (selectedApplications.length === 1) {
        selectedApplicationName = selectedApplications[0].application.name;
    }
    if (selectedApplications.length > 1) {
        selectedApplicationName = t('Common.multiple_applications');
    }

    return (
        <Grid container>
            <Grid item lg={6} xs={12}>
                <Typography variant="h5">
                    {t('Reports.ApplicationReport.map_overview_usage')}
                </Typography>
                <ResponsiveContainer height={350} width="100%">
                    <ComposedChart width="100%" height={250} data={data}>
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <CartesianGrid
                            stroke={theme.palette.chart.axis}
                            strokeDasharray="3 3"
                        />
                        <Bar
                            dataKey="size"
                            name={t('Common.related_app_usage')}
                            barSize={20}
                            fill={theme.palette.graph.colors[0]}
                        />
                        <Line
                            type="natural"
                            name={`${selectedApplicationName} ${t(
                                'Common.usage'
                            )}`}
                            dataKey="baseline"
                            stroke={theme.palette.graph.colors[1]}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </Grid>

            <Grid item lg={6} xs={12}>
                <Typography variant="h5">
                    {t('Reports.ApplicationReport.map_overview_users')}
                </Typography>
                <ResponsiveContainer height={350} width="100%">
                    <ComposedChart width="100%" height={250} data={userData}>
                        <XAxis dataKey="name" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <CartesianGrid
                            stroke={theme.palette.chart.axis}
                            strokeDasharray="3 3"
                        />
                        <Bar
                            dataKey="size"
                            name="Related App Users"
                            barSize={20}
                            fill={theme.palette.graph.colors[0]}
                        />
                        <Line
                            type="natural"
                            name={`${selectedApplicationName} ${t(
                                'Common.users'
                            )}`}
                            dataKey="baselineUsers"
                            stroke={theme.palette.graph.colors[1]}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </Grid>
        </Grid>
    );
};

const headers = (t) => [
    {
        align: 'left',
        field: 'ilo_application',
        renderHeader: <strong>{t('Common.application')}</strong>,
        width: '40%'
    },
    {
        align: 'right',
        field: 'ilo_active',
        renderHeader: <strong>{t('Common.usage')}</strong>,
        width: '15%'
    },
    {
        align: 'right',
        field: 'ilo_add',
        renderHeader: <strong>{t('Common.usageDelta')}</strong>,
        width: '15%'
    },
    {
        align: 'right',
        field: 'users',
        renderHeader: <strong>{t('Common.users')}</strong>,
        width: '15%'
    },
    {
        align: 'right',
        field: 'users_delta',
        renderHeader: <strong>{t('Common.usersDelta')}</strong>,
        width: '15%'
    }
];

const RenderRow = ({ reportData, row, runReport }) => (
    <React.Fragment key={`ilo_${row.application.id}`}>
        <Application data={row.application}>
            <TableRow
                sx={{
                    backgroundColor: 'table.rowBackground'
                }}
            >
                <TableCell>
                    <ApplicationNameAdminAppModalCell
                        onCloseModal={runReport}
                        row={row}
                        nameNoTruncate
                    />
                </TableCell>
                <TableCell align="right">
                    <HMS seconds={row.size} />
                </TableCell>
                <TableCell align="right">
                    {row.size - reportData.baseline > 0 ? (
                        <Typography color="text.success">
                            +{' '}
                            <HMS
                                seconds={Math.abs(
                                    row.size - reportData.baseline
                                )}
                            />
                        </Typography>
                    ) : (
                        <Typography color="text.danger">
                            -{' '}
                            <HMS
                                seconds={Math.abs(
                                    row.size - reportData.baseline
                                )}
                            />
                        </Typography>
                    )}
                </TableCell>
                <TableCell align="right">{row.users}</TableCell>
                <TableCell align="right">
                    {row.users - reportData.baselineUsers > 0 && (
                        <Typography color="text.success">
                            + {row.users - reportData.baselineUsers}
                        </Typography>
                    )}
                    {row.users - reportData.baselineUsers === 0 && (
                        <> &nbsp; 0</>
                    )}
                    {row.users - reportData.baselineUsers < 0 && (
                        <Typography color="text.danger">
                            - {Math.abs(row.users - reportData.baselineUsers)}
                        </Typography>
                    )}
                </TableCell>
            </TableRow>
        </Application>
    </React.Fragment>
);

const ApplicationReportsMapBreakdown = ({
    renderingScheduledReport,
    reportData,
    runReport
}) => {
    const { t } = useTranslation();
    const dataToSort = reportData?.data;
    const data = useMemo(
        () => dataToSort.sort((a, b) => b.size - a.size),
        [dataToSort]
    );
    const tableHeaders = useMemo(() => headers(t), [t]);

    return (
        <Box className="break-before" mt="32px" width="100%">
            <Box width="100%">
                <Typography variant="h5">
                    {t('Reports.ApplicationReport.map_breakdown')}
                </Typography>
            </Box>
            <Box width="100%">
                <ManagedTable
                    headers={tableHeaders}
                    manageRows
                    renderBody={(row) => (
                        <RenderRow
                            reportData={reportData}
                            row={row}
                            runReport={runReport}
                        />
                    )}
                    renderBodyKey="applicationId"
                    renderFooter={!renderingScheduledReport}
                    rows={data}
                    rowsPerPage={-1}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="size"
                />
            </Box>
        </Box>
    );
};
